import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notify } from '../notify';

// "This week's suggestions" — the cards reports and the Monday check
// proposed for this account. Accept applies the change; Decline remembers
// it. Also shows how long suggestions sat untouched (VA activity).
const TYPE_STYLE = {
  drop: ['#ef4444', '🗑'], reduce: ['#f59e0b', '🐢'], increase: ['#22c55e', '🚀'], rest: ['#f59e0b', '⏸'], verify: ['#8b5cf6', '🔒'],
  run_discovery: ['#3b82f6', '🔍'], run_report: ['#60a5fa', '📊'], strategy_cleanup: ['#a78bfa', '🎯'], vault_low: ['#f59e0b', '🗂'], no_content: ['#f59e0b', '🖼'],
  resume_stopped: ['#22c55e', '▶'],
  bulk_remove: ['#ef4444', '🗑'], recheck_removed: ['#f59e0b', '🔄'], reapprove: ['#22c55e', '✅'], remove_dead: ['#ef4444', '☠'],
  approve: ['#22c55e', '✅'], bring_back: ['#22c55e', '🔁'], lift_slowdowns: ['#3b82f6', '🐇'], sibling_overlap: ['#ef4444', '👥'],
  nsfw_ready: ['#22c55e', '🌱'],
  // live posts whose title names another account (informational: leave or delete on Reddit)
  foreign_title: ['#ef4444', '🏷']
};
const ACCEPT_LABEL = { resume_stopped: '▶ Resume all', bulk_remove: '🗑 Remove all', recheck_removed: '🔄 Back to Pending', reapprove: '✅ Re-approve', remove_dead: '🗑 Remove all',
  approve: '✅ Approve', bring_back: '🔁 Bring back', lift_slowdowns: '🐇 Lift all', sibling_overlap: '🗑 Remove all',
  // learned from failed / removed posts (backend/helpers/failureCards.js)
  mod_rule: '📝 Add to sub rules', restricted: '🔐 Waiting for access',
  // Karma farming done (backend/routes/discovery.js leaveKarmaFarming)
  nsfw_ready: '🔞 Move to Warm-up' };

export default function PlannerProposals({ accountId, creatorId, data, onChanged, onRunDiscovery, onShowPending }) {
  const [busy, setBusy] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const proposals = data?.proposals || [];
  const inact = data?.inactivity || {};
  const health = data?.pool_health || null;

  const [bulk, setBulk] = useState(false);
  // Cards that start an AI run (a report, Discovery) cost credits and take a
  // minute or two — Accept all asks separately before starting those.
  const startsRun = (p) => (p.type === 'run_report') || (p.type === 'run_discovery' && !p.payload?.review_only);

  // Returns null when done, else the reason it could not be done.
  const decide = async (p, decision, { quiet = false } = {}) => {
    setBusy(b => ({ ...b, [p.id]: true }));
    let err = null;
    try {
      if (decision === 'accept' && p.type === 'run_report') {
        const rep = p.payload?.report || 'performance';
        // Already made after this card appeared (by hand in AI Reports,
        // or — Content covers the whole creator — by another account's card)?
        // Then don't run (and pay for) it again.
        let fresh = false;
        try {
          const q = rep === 'content' ? `creator_id=${creatorId}&report_type=content` : `creator_id=${creatorId}&account_id=${accountId}&report_type=performance`;
          const h = await axios.get(`${API}/api/reports/performance/history?${q}&limit=1`);
          fresh = !!(h.data?.[0] && new Date(h.data[0].created_at) > new Date(p.created_at));
        } catch (e) { /* run it */ }
        if (!fresh) await axios.post(`${API}/api/reports/${rep}`, { creator_id: creatorId, account_id: accountId, days: p.payload?.days || (rep === 'content' ? 30 : 7) });
      }
      if (decision === 'accept' && p.type === 'run_discovery' && !p.payload?.review_only) {
        if (onRunDiscovery) await onRunDiscovery(p.payload || {});
      }
      const { data: done } = await axios.post(`${API}/api/discovery/planner/proposals/${p.id}/decide`, { decision });
      // Left Karma farming: the approved list was cleared — Discovery finds the
      // new one from the model's content
      if (decision === 'accept' && done?.applied?.run_discovery) {
        notify(`Moved to Warm-up: ${done.applied.cleared || 0} approved subs cleared${done.applied.removed_posts ? ` (and ${done.applied.removed_posts} scheduled posts)` : ''}. Running Discovery for the new list…`, 'success', 8000);
        try { if (onRunDiscovery) await onRunDiscovery({}); notify('Discovery is done: its suggestions are in Pending.', 'success', 8000); }
        catch (e) { notify('Discovery failed: ' + (e.response?.data?.error || e.message) + ' — run it from the Planner.', 'error', 10000); }
      }
      // A rest / slow-down also takes the sub out of days already generated
      const cleared = done?.applied?.removed_posts || 0;
      if (cleared > 0 && !done?.applied?.run_discovery) notify(`${p.title}: also removed ${cleared} scheduled post${cleared === 1 ? '' : 's'} of this sub from upcoming days.`, 'success', 8000);
      if (!quiet) onChanged && onChanged();
    } catch (e) {
      err = e.response?.data?.error || e.message;
      // replaced by an updated card meanwhile (e.g. "Resume 13" became "Resume 10")
      if (quiet && e.response?.data?.status === 'superseded') err = 'SUPERSEDED';
      if (!quiet) {
        alert('Could not ' + decision + ': ' + err);
        // 409 = the card changed meanwhile (replaced, no longer applies,
        // decided by someone else): show the current list
        if (e.response?.status === 409) onChanged && onChanged();
      }
    }
    setBusy(b => ({ ...b, [p.id]: false }));
    return err;
  };

  const acceptAll = async () => {
    // oldest first: if two cards touch the same sub, the newest advice wins
    const changes = proposals.filter(p => !startsRun(p)).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const runs = proposals.filter(startsRun);
    const lines = (list) => list.map(p => `• ${p.title}`).join('\n');
    let doRuns = false;
    if (changes.length) {
      if (!window.confirm(`Accept ${changes.length} suggestion${changes.length === 1 ? '' : 's'}?\n\n${lines(changes)}`)) return;
    }
    if (runs.length) {
      doRuns = window.confirm(`${changes.length ? 'Also start' : 'Start'} ${runs.length} AI run${runs.length === 1 ? '' : 's'}? They use AI credits and take 1–2 minutes each.\n\n${lines(runs)}\n\nCancel = leave them for later.`);
    }
    if (!changes.length && !doRuns) return;
    setBulk(true);
    const failed = [], updated = [];
    const run = async (p) => { const e = await decide(p, 'accept', { quiet: true }); if (e === 'SUPERSEDED') updated.push(p.title); else if (e) failed.push(`• ${p.title}: ${e}`); };
    for (const p of changes) await run(p);
    if (doRuns) for (const p of runs) await run(p);
    setBulk(false);
    onChanged && onChanged();
    const msg = [];
    if (failed.length) msg.push(`${failed.length} suggestion${failed.length === 1 ? '' : 's'} could not be applied:\n\n${failed.join('\n')}`);
    if (updated.length) msg.push(`${updated.length} suggestion${updated.length === 1 ? ' was' : 's were'} updated by the changes above (e.g. a new count) — ${updated.length === 1 ? 'it is' : 'they are'} in the list again to accept.`);
    if (msg.length) alert(msg.join('\n\n'));
  };

  const refresh = async () => {
    setRefreshing(true);
    try { await axios.post(`${API}/api/discovery/planner/${accountId}/refresh-proposals`); onChanged && onChanged(); }
    catch (e) { alert('Refresh failed: ' + (e.response?.data?.error || e.message)); }
    setRefreshing(false);
  };

  // Posts 2+ days old with no views reading: the reports can't judge them, and
  // after 10 days they can't be read at all (moonrose14: none read 27 Sep–1 Oct)
  const unread = data?.unread_views;
  const [readBusy, setReadBusy] = useState(false);
  const readStats = async () => {
    setReadBusy(true);
    try {
      await axios.post(`${API}/api/extension/commands`, { account_id: accountId, type: 'post_stats' });
      notify("Sent to the account's browser — it starts reading within ~30 s (up to 60 posts a run).", 'success', 8000);
    } catch (e) { alert('Could not send: ' + (e.response?.data?.error || e.message)); }
    setReadBusy(false);
  };

  const untouched = inact.open_count > 0 ? inact.oldest_open_days : 0;
  const activityColor = untouched >= 7 ? '#ef4444' : untouched >= 3 ? '#f59e0b' : '#22c55e';

  return (
    <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#0f0f14', borderRadius: '10px', border: `1px solid ${proposals.length ? '#1e3a5f' : '#2a2a2a'}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: proposals.length || unread?.count ? '10px' : 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>💡 This week's suggestions{proposals.length ? ` (${proposals.length})` : ''}</div>
        <span title="Oldest open suggestion / last decision — shows whether suggestions are being acted on" style={{ fontSize: '11px', color: activityColor }}>
          {inact.open_count > 0
            ? `⏱ waiting ${untouched} day${untouched === 1 ? '' : 's'} without a decision`
            : inact.last_decided_at ? `✓ last decision ${inact.days_since_last_decision}d ago${inact.last_decided_by ? ' by ' + inact.last_decided_by : ''}` : 'nothing suggested yet'}
        </span>
        {inact.pending_discovery > 0 && (
          <span onClick={onShowPending} role={onShowPending ? 'button' : undefined}
            style={{ fontSize: '11px', color: inact.pending_oldest_days >= 7 ? '#ef4444' : '#f59e0b', cursor: onShowPending ? 'pointer' : 'default', textDecoration: onShowPending ? 'underline dotted' : 'none' }}
            title="Subs Discovery suggested that nobody approved or rejected yet (Pending + Verification tabs). Click to open them.">
            · {inact.pending_discovery} sub{inact.pending_discovery === 1 ? '' : 's'} from Discovery await approval{inact.pending_oldest_days ? ` (oldest ${inact.pending_oldest_days}d)` : ''}
          </span>
        )}
        {data?.karma?.stage === 'comments' ? (
          <span style={{ fontSize: '11px', color: '#f59e0b', marginLeft: 'auto' }} title="Karma farming: no Discovery and no subs until the comment karma target">
            💬 comments only until {data.karma.comment_target} comment karma — no subs needed yet
          </span>
        ) : health && (
          <span style={{ fontSize: '11px', color: health.short_by > 0 ? '#f59e0b' : '#22c55e', marginLeft: 'auto' }}
            title={`At the pace each sub's audience can take (penetration), the ${health.postable} subs in rotation have room for ${health.capacity} posts a week; the strategy needs ${health.demand} (${Math.round(health.demand / 7)}/day). Days they can't fill get new subs, second chances and extra posts — new subreddits are the lasting fix, mostly mid-size (100–300K weekly visitors).`}>
            {health.postable} subs in rotation · room for {health.capacity}/{health.demand} posts a week{health.short_by > 0 ? ` · new subreddits needed: ~${health.subs_short}, mostly mid-size` : ' · healthy'}{health.awaiting_verification > 0 ? ` · ${health.awaiting_verification} approved await verification (not counted)` : ''}{health.stopped > 0 ? ` · ${health.stopped} stopped (not counted)` : ''}
          </span>
        )}
        {proposals.length > 1 && (
          <button className="planner-solid" onClick={acceptAll} disabled={bulk} style={{ background: '#2563eb' }}
            title="Apply every suggestion below. Report / Discovery runs are confirmed separately.">
            {bulk ? 'Applying…' : `✓ Accept all (${proposals.length})`}
          </button>
        )}
        <button className="planner-ghost" onClick={refresh} disabled={refreshing} title="Recompute the Monday cards now (pool health, reports ready to run, strategy cleanup)">
          {refreshing ? '…' : '↻ check now'}
        </button>
        <button className="planner-ghost" onClick={() => setShowHistory(h => !h)}>
          {showHistory ? 'hide history' : `history (${(data?.decisions || []).length})`}
        </button>
      </div>

      {unread?.count > 0 && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 10px', marginBottom: '8px', background: '#1a1408', border: '1px solid #5a4400', borderRadius: '8px', fontSize: '12px', color: '#f59e0b' }}>
          <span style={{ flex: 1 }}>
            ⚠ <b>{unread.count} post{unread.count === 1 ? '' : 's'} 2+ days old {unread.count === 1 ? 'has' : 'have'} no views reading</b> — press "Update post stats" in this account's browser.
            {' '}Reports can't judge unread posts{unread.lost_after ? `, and the oldest can't be read after ${new Date(unread.lost_after + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })}` : ''}.
          </span>
          <button className="planner-ghost" onClick={readStats} disabled={readBusy} title="Asks this account's open browser (Dolphin profile with the extension) to read its posts' stats now">
            {readBusy ? '…' : '📊 Update post stats now'}
          </button>
        </div>
      )}

      {proposals.map(p => {
        const [color, icon] = TYPE_STYLE[p.type] || ['#888', '•'];
        const age = Math.floor((Date.now() - new Date(p.created_at).getTime()) / 864e5);
        return (
          <div key={p.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '8px 10px', marginBottom: '6px', background: '#141418', borderRadius: '8px', borderLeft: `3px solid ${color}` }}>
            <span style={{ fontSize: '16px' }}>{icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', color: '#fff', fontWeight: 600 }}>{p.title}</div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{p.reason}</div>
              {Array.isArray(p.payload?.items) && p.payload.items.length > 0 && (
                <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                  {p.payload.items.slice(0, 25).map((it, i) => (
                    <div key={i} style={{ fontSize: '11px', color: '#bbb' }}>
                      <span style={{ color: '#fff' }}>{it.name}</span>{it.note ? <span style={{ color: '#888' }}> — {it.note}</span> : null}
                    </div>
                  ))}
                  {p.payload.items.length > 25 && <div style={{ fontSize: '11px', color: '#888' }}>… +{p.payload.items.length - 25} more</div>}
                </div>
              )}
              <div style={{ fontSize: '10px', color: '#555', marginTop: '2px' }}>
                from {p.source}{age > 0 ? ` · ${age}d ago` : ' · today'}
                {p.metrics?.confidence && <span style={{ color: /^low/i.test(p.metrics.confidence) ? '#f59e0b' : '#555' }} title="How much data backs the AI's verdict"> · AI confidence {String(p.metrics.confidence).split(/[\s—|]/)[0]}</span>}
              </div>
            </div>
            <button onClick={() => decide(p, 'accept')} disabled={busy[p.id]} style={{ fontSize: '11px', padding: '5px 12px', borderRadius: '6px', border: 'none', background: color, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {busy[p.id] ? '…' : p.type === 'run_discovery' ? (p.payload?.review_only ? 'OK' : '🔍 Run') : p.type === 'run_report' ? '📊 Run' : ['vault_low', 'no_content', 'set_flair', 'fix_titles', 'account_filtered', 'foreign_title'].includes(p.type) ? 'OK' : ACCEPT_LABEL[p.type] || '✓ Accept'}
            </button>
            <button onClick={() => decide(p, 'decline')} disabled={busy[p.id]} style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #333', background: 'transparent', color: '#888', cursor: 'pointer' }}>✕</button>
          </div>
        );
      })}

      {showHistory && (
        <div style={{ marginTop: '8px', borderTop: '1px solid #222', paddingTop: '8px' }}>
          {(data?.decisions || []).length === 0 && <div style={{ fontSize: '11px', color: '#555' }}>No decisions yet.</div>}
          {(data?.decisions || []).map(d => (
            <div key={d.id} style={{ fontSize: '11px', color: '#888', padding: '3px 0', display: 'flex', gap: '8px' }}>
              <span style={{ color: d.status === 'accepted' ? '#22c55e' : d.status === 'expired' ? '#666' : '#ef4444', width: '62px' }}
                title={d.status === 'expired' ? 'Closed automatically — its reason was gone' : undefined}>{d.status === 'expired' ? 'closed' : d.status}</span>
              <span style={{ flex: 1, color: '#bbb' }}>{d.title}{d.decline_note ? ` — "${d.decline_note}"` : ''}</span>
              <span>{d.status === 'expired' ? 'auto' : (d.decided_by_name || '?')} · {new Date(d.decided_at).toLocaleDateString('en-GB')}</span>
              {d.outcome && <span title={JSON.stringify(d.outcome)} style={{ color: '#60a5fa' }}>{d.outcome.note ? d.outcome.note : `${d.outcome.avg_views_before != null ? `${d.outcome.avg_views_before} → ` : 'now '}${d.outcome.avg_views} avg views${d.outcome.change_pct != null ? ` (${d.outcome.change_pct > 0 ? '+' : ''}${d.outcome.change_pct}%)` : ''}${d.outcome.posts_since ? ` · ${d.outcome.posts_since} posts since` : ''}`}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
